/// <amd-module name="Wit/Common/WorkItemIdentityService/IWorkItemIdentityService" />
import {
    IIdentity,
    IVssIdentityService,
    IdentitiesSearchRequestModel,
    VssIdentityService,
    defaultQueryProperties
} from "VSS/Features/Identities/IdentityService";
import { IVssPageContext, ServiceOptions, Services } from "VSS/Platform/Context";
import { getAllowedIdentityTypes } from "Wit/Common/Utilities/Identity";

export interface IWorkItemImportIdentityService extends IVssIdentityService {
    /**
     * Given a search request model, return a list of Entities (per query token). If the filterIdentity callback is added, additionally filter the values before returning
     *
     * @param query The query to search the identities type with.
     * @param identityTypes The types of identities to search (default "user" and "group")
     * @param operationScopes The scope you would like to search (default "ims", "source")
     * @param queryTypeHint A hint of what property your query is trying to search
     * @param filterIdentity A filter passed in to alter the results of the identities found
     * @param options Additional options to pass into the search
     * @returns The list of filtered identities from the search.
     */
    searchIdentitiesAsync(
        query: string,
        identityTypes?: string[],
        operationScopes?: string[],
        queryTypeHint?: string,
        options?: any,
        filterIdentity?: (returnedEntities: IIdentity[]) => IIdentity[],
        backlogTreeLoadingOptimizationIsEnabled?: boolean,
        backlogDelayLoadingIdentityIsEnabled?: boolean
    ): Promise<IImportIdentity[]>;
}

interface IImportIdentity extends IIdentity {
    queryToken?: string;
}

export const WorkItemIdentityServiceName = "IWorkItemImportIdentityService";

export class WorkItemImportIdentityService extends VssIdentityService {
    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);
    }

    public _serviceEnd(pageContext: IVssPageContext) {
        super._serviceEnd(pageContext);
    }

    public async searchIdentitiesAsync(
        query: string,
        identityTypes?: string[],
        operationScopes?: string[],
        queryTypeHint?: string,
        options?: any,
        filterIdentity?: (returnedEntities: IIdentity[]) => IIdentity[],
        backlogTreeLoadingOptimizationIsEnabled?: boolean,
        backlogDelayLoadingIdentityIsEnabled?: boolean
    ): Promise<IImportIdentity[]> {
        const requestProps: IdentitiesSearchRequestModel = this.getRequestModel(query, identityTypes, operationScopes, queryTypeHint, options);
        const rootPath = await this._identityPickerClient.getRootPathPromise();
        const identityList = await this._identityPickerClient.beginGetIdentities(requestProps);

        const map = new Map<string, Map<string, IImportIdentity>>();
        const getMatchingMruIdentities = this._createMruFilter(requestProps);
        for (const resultSet of identityList.results) {
            const identites = getMatchingMruIdentities(resultSet.queryToken);
            this.updateMap(resultSet.queryToken, map, identites);
        }

        if (backlogTreeLoadingOptimizationIsEnabled && backlogDelayLoadingIdentityIsEnabled) {
            // Add the retrieved identities (with additional identity info).
            // If a matching MRU value exists, don't overwrite it with the retrieved values as this could
            // cause UI to suddenly switch display name if the retrieved value had changed on the server
            const individaulResultPromises = identityList.results.map(async resultSet => {
                if (resultSet.identities.length === 0) {
                    const cachedIdentities = getMatchingMruIdentities(resultSet.queryToken);
                    if (cachedIdentities?.length) {
                        this.updateMap(resultSet.queryToken, map, cachedIdentities);
                        return;
                    }

                    // If there are no cached identities, then we need to make a call to the server to get the identities
                    const requestArgs: IdentitiesSearchRequestModel = this.getRequestModel(
                        resultSet.queryToken,
                        identityTypes,
                        operationScopes,
                        queryTypeHint,
                        options,
                        resultSet.pagingToken
                    );

                        const identity = await this._identityPickerClient.beginGetIdentities(requestArgs);
                        identity.results.forEach(result => {
                            this.setIdentities(result.identities, map, rootPath, resultSet.queryToken);
                        });

                } else {
                    this.setIdentities(resultSet.identities, map, rootPath, resultSet.queryToken);
                }
            });
            await Promise.all(individaulResultPromises);
        } else {
            for (const resultSet of identityList.results) {
                if (resultSet.identities.length === 0) {
                    const cachedIdentities = getMatchingMruIdentities(resultSet.queryToken);
                    if (cachedIdentities?.length) {
                        this.updateMap(resultSet.queryToken, map, cachedIdentities);
                        continue;
                    }

                    // If there are no cached identities, then we need to make a call to the server to get the identities
                    const requestArgs: IdentitiesSearchRequestModel = this.getRequestModel(
                        resultSet.queryToken,
                        identityTypes,
                        operationScopes,
                        queryTypeHint,
                        options,
                        resultSet.pagingToken
                    );

                    if (backlogTreeLoadingOptimizationIsEnabled && !backlogDelayLoadingIdentityIsEnabled) {
                        (async () => {
                            const identity = await this._identityPickerClient.beginGetIdentities(requestArgs);
                            identity.results.forEach(result => {
                                this.setIdentities(result.identities, map, rootPath, resultSet.queryToken);
                            });
                        })();
                    } else {
                        const identity = await this._identityPickerClient.beginGetIdentities(requestArgs);
                        identity.results.forEach(result => {
                            this.setIdentities(result.identities, map, rootPath, resultSet.queryToken);
                        });
                    }

                } else {
                    this.setIdentities(resultSet.identities, map, rootPath, resultSet.queryToken);
                }
            }
        }

        const identities: IIdentity[] = [...map.values()].flatMap(identitySet => [...identitySet.values()]);
        return filterIdentity ? filterIdentity(identities) : identities;
    }

    /**
     * Updates the given map with the provided identities, using the specified query token.
     * If the query token is already present in the map, the new identities will be added to the existing map without ovewriting any existing identities.
     * If the query token is not present in the map, a new map will be created with the provided identities.
     * @param queryToken The query token to use for the identities.
     * @param map The map to update with the identities.
     * @param identites The identities to add to the map.
     * @param identityCallback An optional callback function to modify the identities before adding them to the map.
     */
    private updateMap(
        queryToken: string,
        map: Map<string, Map<string, IImportIdentity>>,
        identites: IIdentity[],
        identityCallback?: (identity: IIdentity) => IIdentity
    ) {
        const getIdentity = (importIdentity: IImportIdentity, token: string): IIdentity => {
            const identity = identityCallback ? { ...identityCallback(importIdentity) } : importIdentity;
            identity.queryToken = token;
            return identity;
        };

        if (map.has(queryToken)) {
            const existingMap = map.get(queryToken)!;
            identites
                .filter(identity => !existingMap.has(identity.entityId))
                .forEach(identity => {
                    existingMap.set(identity.entityId, getIdentity(identity, queryToken));
                });
        } else {
            map.set(queryToken, new Map<string, IImportIdentity>(identites.map(identity => [identity.entityId, getIdentity(identity, queryToken)])));
        }
    }

    private getRequestModel(
        queryToken: string,
        identityTypes: string[] | undefined,
        operationScopes: string[] | undefined,
        queryTypeHint: string | undefined,
        options: any,
        pagingToken: string | undefined = undefined
    ): IdentitiesSearchRequestModel {
        return {
            query: queryToken,
            pagingToken: pagingToken,
            identityTypes: identityTypes || getAllowedIdentityTypes(this.pageContext),
            operationScopes: operationScopes || ["ims", "source"],
            queryTypeHint: queryTypeHint,
            options: {
                MinResults: 5,
                MaxResults: 40,
                ...options
            },
            properties: defaultQueryProperties
        };
    }

    protected _createMruFilter(requestProps: IdentitiesSearchRequestModel): (queryToken: string) => IIdentity[] {
        const { _mruCache } = this;
        if (_mruCache) {
            let byIdentityType = (identity: IIdentity) => true;
            if (requestProps.identityTypes) {
                const idTypeSet = new Set(requestProps.identityTypes.map(t => t.toLocaleLowerCase()));
                byIdentityType = identity => idTypeSet.has(identity.entityType.toLocaleLowerCase());
            }

            return (queryToken: string) => {
                queryToken = queryToken.toLowerCase();
                return _mruCache
                    .filter(byIdentityType)
                    .filter(
                        identity =>
                            (identity.displayName && identity.displayName.toLowerCase().startsWith(queryToken)) ||
                            (identity.mail && identity.mail.toLowerCase() === queryToken) ||
                            (identity.signInAddress && identity.signInAddress.toLowerCase() === queryToken) ||
                            (identity.samAccountName && identity.samAccountName.toLowerCase() === queryToken)
                    );
            };
        }

        return () => [];
    }

    private setIdentities(identities: IIdentity[], map: Map<string, Map<string, IImportIdentity>>, rootPath: string, queryToken: string) {
        this.updateMap(queryToken, map, identities, (identity: IIdentity) => this._addAdditionalIdentityInfo(identity, rootPath));
    }
}

Services.add(WorkItemIdentityServiceName, { serviceFactory: WorkItemImportIdentityService, options: ServiceOptions.Remotable });
